import {
  EmailCampaignStatus,
  EmailCampaignType,
  ExemptionCertificateSubmission,
  ExemptionCertificateTemplate,
  ExemptionCustomerSellerRelationship,
  ExemptionGenerationType,
  ExemptionInvitationStatus,
  ExemptionSubmissionStatus,
  RootfiIntegrationCategory,
} from '@prisma/client';

// INPUTS
export type UpdateUserDTO = {
  firstName?: string;
  lastName?: string;
};

export type AcceptInvitationDTO = {
  requestId: string;
};

export type CorporationIdQueryDTO = {
  corporation_id: number;
};

export type FileUploadDTO = {
  fileUrl?: string;
  base64Data?: string;
  fileName?: string;
  mimeType?: string;
};

export type CreateSubmissionDTO = {
  customerSellerRelationshipId: string;
  templateId: string;
  formData: Record<string, any>;
  certificateFileUrl?: string;
  supportingDocUrls?: string[];
};

export type UpdateSubmissionDTO = {
  formData?: Record<string, any>;
  certificateFileUrl?: string;
  supportingDocUrls?: string[];
};

export type ReviewSubmissionDTO = {
  approved: boolean;
  rejectionReason?: string;
  rejectionDetails?: Record<string, string>;
};

export type CreateTemplateDTO = {
  corporationId: number;
  name: string;
  exemptionReason: string;
  jurisdiction: string;
  pdfTemplateUrl: string;
  // THESE FIELDS WILL GET TYPED ONCE WE START WORKING ON TEMPLATES. FOR NOW, WE DO NOT USE THEM.
  availableFields: Record<string, any>;
  requiredFields: Record<string, any>;
  conditionalFields?: Record<string, any>;
  fieldGroups: Record<string, any>;
  validationRules?: Record<string, any>;
  // ------------------------------------------------------------
  generationType?: ExemptionGenerationType;
  helpText?: string;
  isActive?: boolean;
};

export type UpdateTemplateDTO = {
  corporationId: number;
  name?: string;
  exemptionReason?: string;
  jurisdiction?: string;
  pdfTemplateUrl?: string;
  // THESE FIELDS WILL GET TYPED ONCE WE START WORKING ON TEMPLATES. FOR NOW, WE DO NOT USE THEM.
  availableFields?: Record<string, any>;
  requiredFields?: Record<string, any>;
  conditionalFields?: Record<string, any>;
  fieldGroups?: Record<string, any>;
  validationRules?: Record<string, any>;
  // ------------------------------------------------------------
  generationType?: ExemptionGenerationType;
  helpText?: string;
  isActive?: boolean;
};

// OUTPUTS
export type LoginResponse = {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  tokenData: {
    exp: number;
  };
};

export type UserResponse = {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    createdAt: Date;
    lastLogin: Date;
  };
};

export type CreateInvitationResponse = {
  id: string;
  requestId: string;
  status: string;
  invitedAt: Date;
  customerName: string;
  externalCustomerId: string;
};

export type AcceptInvitationResponse = {
  id: string;
  sellerId: number;
  sellerName: string;
  status: string;
  acceptedAt: Date;
};

export type ActivateInvitationResponse = {
  id: string;
  sellerId: number;
  sellerName: string;
  status: ExemptionInvitationStatus;
  acceptedAt: Date;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
};

export type CustomerSellerRelationship = {
  id: string;
  sellerId: number;
  sellerName: string;
  customerName: string;
  status: ExemptionInvitationStatus;
  isActive: boolean;
  invitedAt: Date;
  requestId: string;
  acceptedAt: Date;
  externalCustomerId: string;
  certificates: {
    total: number;
    pending: number;
    rejected: number;
    lastActivity: Date | null;
  };
};

export type SellerCustomerRelationship = {
  id: string;
  requestId: string;
  customerId: string;
  customerName: string;
  status: ExemptionInvitationStatus;
  isActive: boolean;
  invitedAt: Date;
  acceptedAt: Date;
  sourcePlatform?: RootfiIntegrationCategory;
  sourcePlatformId?: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  certificates: {
    total: number;
    pending: number;
    rejected: number;
    lastActivity: Date | null;
  };
};
export interface BulkActionCustomersParams {
  corporation_id: number;
  customer_ids?: string[];
  excluded_ids?: string[];
  search_term?: string;
}

export interface SellerCustomerRelationshipListData {
  customers: SellerCustomerRelationship[];
  cursor?: string;
  total_customers: number;
}
export interface SellerCustomerRelationshipListResponse {
  data: SellerCustomerRelationshipListData;
  message: string;
}

export type CustomerSellerRelationshipByIdResponse = {
  id: string;
  sellerId: number;
  sellerName: string;
  sellerAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zipCode: string;
  };
  customerId: string;
  customerName: string;
  customerAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zipCode: string;
  };
  status: ExemptionInvitationStatus;
  isActive: boolean;
  invitedAt: Date;
  acceptedAt: Date;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    clerkUserId: string;
  } | null;
  certificates: Array<{
    id: string;
    status: ExemptionSubmissionStatus;
    createdAt: Date;
    updatedAt: Date;
    externalCertificateId: string;
  }>;
};

export type CreateSubmissionResponse = {
  id: string;
  status: ExemptionSubmissionStatus;
  createdAt: Date;
  updatedAt: Date;
  customerSellerRelationshipId: string;
  templateId: string;
  certificateFileUrl: string;
  supportingDocUrls: string[];
  externalCertificateId: string;
};
export type UpdateSubmissionResponse = {
  id: string;
  status: ExemptionSubmissionStatus;
  createdAt: Date;
  updatedAt: Date;
  submittedAt?: Date;
  revisionCount?: number;
  certificateFileUrl: string;
  supportingDocUrls: string[];
  externalCertificateId: string;
};

export type ReviewSubmissionResponse = {
  id: string;
  status: ExemptionSubmissionStatus;
  reviewedAt: Date;
  externalCertificateId: string;
  rejectionReason?: string;
  rejectionDetails?: any;
};

export type GetSubmissionResponse = ExemptionCertificateSubmission & {
  customerSellerRelationship: Partial<ExemptionCustomerSellerRelationship>;
  template?: Partial<ExemptionCertificateTemplate>;
  derivedStatus?: SubmissionStatusFilter;
};

export enum SubmissionStatusFilter {
  APPROVED = 'APPROVED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
  REJECTED = 'REJECTED',
}

export type CertificateSubmissionDetails = {
  submissionId: string;
  submissionStatus: string;
  createdAt: Date;
  updatedAt: Date;
  templateName: string;
  templateJurisdiction: string;
  exemptionReason: string;
  rejectionReason: string;
  externalCertificateId: string;
  lastSyncedToSalesTaxAt: Date;
};

export type CertificateForPortalUserResponse = {
  sellerId: number;
  sellerName: string;
  certificates: CertificateSubmissionDetails[];
};

export type CertificateForSellerResponse = {
  customerId: string;
  customerName: string;
  certificates: CertificateSubmissionDetails[];
};

export interface SendExemptionInvitationResponse {
  campaignId: string;
  relationshipsCreated: number;
  invitationsQueued: number;
  batchesQueued: number;
}

export type CampaignListItem = {
  id: string;
  name: string;
  description: string;
  type: EmailCampaignType;
  status: EmailCampaignStatus;
  totalEmails: number;
  sentEmails: number;
  failedEmails: number;
  skippedEmails: number;
  createdAt: Date;
  updatedAt: Date;
};

export type GetCampaignListParams = {
  corporationId: number;
  type: EmailCampaignType;
};
